import { isSupSubNode } from "@/models/utils/semanticChecks";

import { filterBySignature, getMatchVariants, notationsForAnchor } from "./getMatchVariants";

import type { CursorModel } from "@/models/CursorModel";
import type { NodeModel } from "@/models/nodes/types";
import type { SelectionModel } from "@/models/SelectionModel";
import type { SwitchNotationsJsonItem } from "@corca/data";

/** Upper bound on how many sibling nodes a single notation match can span. */
const MAX_SPAN = 12;

/**
 * Locates the notation expression around the cursor when there is no selection.
 *
 * The cursor is treated as the RIGHT side of the match: we look at ranges of sibling nodes
 * that either end at the cursor (`y/z|`, `x²|`) or, when the cursor sits just after a concrete
 * anchor operator, extend past it to include the right operand (`y/|z`). The shortest matching
 * range wins, so we prefer the tightest expression at the cursor (e.g. `x²` inside `a + x²`).
 *
 * Returns the matched node range plus the candidate notations, in the same shape selection mode
 * produces, so the caller can hand both to `getMatchVariants` unchanged.
 */
export function matchNotationNodesAtCursor(
  siblings: NodeModel[],
  cursorIndex: number,
): { nodes: NodeModel[]; notations: SwitchNotationsJsonItem[] } | null {
  for (const range of candidateRanges(siblings, cursorIndex)) {
    const notations = filterBySignature(range);

    if (notations.length > 0 && getMatchVariants(range, notations).length > 0) {
      return { nodes: range, notations };
    }
  }

  return null;
}

type NotationMatch = {
  nodes?: NodeModel[];
  notations?: SwitchNotationsJsonItem[];
};

export function matchNotations(cursor: CursorModel, selection: SelectionModel): NotationMatch {
  if (selection.hasSelectedNodes) {
    const { selectedNodes } = selection;
    return { nodes: selectedNodes, notations: filterBySignature(selectedNodes) };
  }

  if (cursor.node && cursor.index !== undefined) {
    return matchNotationNodesAtCursor(cursor.node.nodes, cursor.index) ?? {};
  }

  return {};
}

function candidateRanges(siblings: NodeModel[], cursor: number): NodeModel[][] {
  const endingAtCursor = rangesEndingAt(siblings, cursor, cursor - 1);
  const anchor = cursor > 0 ? siblings[cursor - 1] : undefined;
  const rightOperandEnd = cursor + operandLength(siblings, cursor);
  const spanningAnchor =
    anchor && notationsForAnchor(anchor).length > 0 && rightOperandEnd > cursor
      ? rangesEndingAt(siblings, rightOperandEnd, cursor - 2)
      : [];

  return [...endingAtCursor, ...spanningAnchor];
}

function rangesEndingAt(siblings: NodeModel[], end: number, maxStart: number): NodeModel[][] {
  const ranges: NodeModel[][] = [];
  const lowest = Math.max(0, end - MAX_SPAN);

  for (let start = Math.min(maxStart, end - 1); start >= lowest; start--) {
    if (isSupSubNode(siblings[start])) {
      continue;
    }

    ranges.push(siblings.slice(start, end));
  }

  return ranges;
}

/**
 * Length of the operand (base node + trailing supsub operators) starting at `start`.
 */
function operandLength(siblings: NodeModel[], start: number): number {
  if (start >= siblings.length) {
    return 0;
  }

  let length = 1;
  while (start + length < siblings.length && isSupSubNode(siblings[start + length])) {
    length++;
  }

  return length;
}
